/**
 * Smart wrapper for FieldEditHistory. Fetches the per-field edit history
 * via React Query + the tenant-scoped Eden client; FieldEditHistoryView
 * renders the drawer UI.
 */

import type { IFieldEdit } from "@lexicanon/shared"
import { useMemo } from "react"
import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import { fetchFieldEdits } from "../../lib/api"
import { queryKeys } from "../../lib/query-keys"
import { FieldEditHistoryView } from "./FieldEditHistoryView"
import type { IProps } from "./interfaces"

export function FieldEditHistory({ meetingId, fieldPath, opened, onClose }: IProps): React.JSX.Element {
  const { data, isLoading } = useCompanyQuery({
    key: (org) => queryKeys.fieldEdits(org, meetingId),
    queryFn: (company) => fetchFieldEdits(company, meetingId),
    enabled: opened,
  })

  const edits: IFieldEdit[] = useMemo(() => {
    const raw = data as { edits?: Record<string, IFieldEdit[]> } | undefined
    return raw?.edits?.[fieldPath] ?? []
  }, [data, fieldPath])

  return (
    <FieldEditHistoryView fieldPath={fieldPath} opened={opened} onClose={onClose} edits={edits} isLoading={isLoading} />
  )
}
